import {Alert, Button, OtpCodeField, Typography} from '@sollapay/ui/components';
import {formatCountdown, maskEmail} from '@sollapay/utils';
import {Trans, useTranslation} from 'react-i18next';

import {getTranslatedErrorMessage} from '@/utils';

import {CreatePayoutStepLayout} from './CreatePayoutStepLayout';

import type {FC} from 'react';

export interface VerificationStepProps {
  code: string;
  onCodeChange: (code: string) => void;
  email: string;
  saveError?: unknown;
  onResend: () => void;
  isResending?: boolean;
  resendCooldownSeconds?: number;
}

export const VerificationStep: FC<VerificationStepProps> = ({
  code,
  onCodeChange,
  email,
  saveError,
  onResend,
  isResending,
  resendCooldownSeconds = 0
}) => {
  const {t} = useTranslation('trustAccounts');
  const {t: tCommon} = useTranslation('common');
  const prefix = 'view.payouts.create.verification';
  const hasError = saveError != null;

  return (
    <CreatePayoutStepLayout
      title={t(`${prefix}.title`)}
      subtitle={
        <Trans
          t={t}
          i18nKey={`${prefix}.subtitle`}
          values={{email: maskEmail(email)}}
          components={{bold: <strong className="font-semibold text-fg-secondary" />}}
        />
      }
    >
      <div className="flex flex-col gap-3">
        <OtpCodeField
          label={t(`${prefix}.codeLabel`)}
          value={code}
          onChange={onCodeChange}
          error={hasError}
        />

        <ResendRow
          onResend={onResend}
          isResending={isResending}
          cooldownSeconds={resendCooldownSeconds}
          resendLabel={t(`${prefix}.resend`)}
        />

        {hasError && (
          <Alert
            variant="error"
            closable
            title={getTranslatedErrorMessage(saveError, tCommon)}
            description={t(`${prefix}.errorHint`)}
          />
        )}
      </div>
    </CreatePayoutStepLayout>
  );
};

interface ResendRowProps {
  onResend: () => void;
  isResending?: boolean;
  cooldownSeconds: number;
  resendLabel: string;
}

function ResendRow({onResend, isResending, cooldownSeconds, resendLabel}: ResendRowProps) {
  const disabled = cooldownSeconds > 0 || isResending;

  return (
    <div className="flex w-full items-center justify-end gap-2">
      <Button
        variant="linkColor"
        size="sm"
        className="h-auto p-0"
        onClick={onResend}
        disabled={disabled}
      >
        {resendLabel}
      </Button>
      {cooldownSeconds > 0 && (
        <Typography size="xs" color="tertiary" dir="ltr">
          {formatCountdown(cooldownSeconds)}
        </Typography>
      )}
    </div>
  );
}
